/**
 * useWithdrawMeta Hook
 * 구분코드, 수수료, 계좌 정보 자동 조회 전용 훅
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { commonCodeApi } from '@/shared/api/commonCodeApi';
import { CommonCodeDto } from '@/shared/types/commonCode.types';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';

export const useWithdrawMeta = () => {
    const { showToast: _showToast } = useToast();
    const [wdTypes, setWdTypes] = useState<CommonCodeDto[]>([]);
    const [withdrawFee, setWithdrawFee] = useState<number>(300);
    const [depositFee, setDepositFee] = useState<number>(300);
    const [accountInfo, setAccountInfo] = useState<{
        depositBankCode?: string;
        depositAcctNum?: string;
        depositAcctName?: string;
        withdrawBankCode?: string;
        withdrawAcctNum?: string;
        withdrawAcctName?: string;
    } | null>(null);

    const hasFetchedWdTypes = useRef(false);
    const hasFetchedWithdrawFee = useRef(false);
    const hasFetchedDepositFee = useRef(false);
    const hasFetchedAccountInfo = useRef(false);

    // 구분 코드 목록 조회
    useEffect(() => {
        if (hasFetchedWdTypes.current) return;

        let isCancelled = false;

        const fetchWdTypes = async () => {
            try {
                const response = await commonCodeApi.getCommonCodeList('WD_TYPE');
                if (!isCancelled) {
                    setWdTypes(response.codes || []);
                    hasFetchedWdTypes.current = true;
                }
            } catch {
                // 에러는 무시
            }
        };

        fetchWdTypes();
        return () => {
            isCancelled = true;
        };
    }, []);

    // 출금수수료 조회
    useEffect(() => {
        if (hasFetchedWithdrawFee.current) return;

        let isCancelled = false;

        const fetchWithdrawFee = async () => {
            try {
                const response = await commonCodeApi.getCommonCodeList('WITHRAW_FEE_AMT');
                if (!isCancelled && response.codes) {
                    const withdrawCode = response.codes.find(
                        code => code.comCode === 'WITHRAW' || code.comCdName === '출금'
                    );
                    if (withdrawCode && withdrawCode.val1) {
                        const fee = parseInt(withdrawCode.val1, 10);
                        if (!isNaN(fee)) {
                            setWithdrawFee(fee);
                        }
                    }
                    hasFetchedWithdrawFee.current = true;
                }
            } catch {
                // 에러는 무시
            }
        };

        fetchWithdrawFee();
        return () => {
            isCancelled = true;
        };
    }, []);

    // 입금수수료 조회
    useEffect(() => {
        if (hasFetchedDepositFee.current) return;

        let isCancelled = false;

        const fetchDepositFee = async () => {
            try {
                const response = await commonCodeApi.getCommonCodeList('WITHRAW_FEE_AMT');
                if (!isCancelled && response.codes) {
                    const depositCode = response.codes.find(
                        code => code.comCode === 'DEPOSIT' || code.comCdName === '입금'
                    );
                    if (depositCode && depositCode.val1) {
                        const fee = parseInt(depositCode.val1, 10);
                        if (!isNaN(fee)) {
                            setDepositFee(fee);
                        }
                    }
                    hasFetchedDepositFee.current = true;
                }
            } catch {
                // 에러는 무시
            }
        };

        fetchDepositFee();
        return () => {
            isCancelled = true;
        };
    }, []);

    // 계좌 정보 조회
    const fetchAccountInfo = useCallback(async (forceRefresh = false) => {
        if (!forceRefresh && hasFetchedAccountInfo.current) return;

        try {
            const response = await commonCodeApi.getCommonCodeList('VR_ACCOUNT_NUM');
            const codes = response.codes || [];

            const accountData: {
                depositBankCode?: string;
                depositAcctNum?: string;
                depositAcctName?: string;
                withdrawBankCode?: string;
                withdrawAcctNum?: string;
                withdrawAcctName?: string;
            } = {};

            codes.forEach((code: CommonCodeDto) => {
                const codeUpper = (code.comCode || '').toUpperCase();
                const nameUpper = (code.comCdName || '').toUpperCase();

                if (codeUpper.includes('DEPOSIT') || nameUpper.includes('입금')) {
                    accountData.depositBankCode = code.val1 || '';
                    accountData.depositAcctNum = code.val2 || '';
                    accountData.depositAcctName = code.val3 || code.comCdName || '';
                } else if (codeUpper.includes('WITHDRAW') || nameUpper.includes('출금')) {
                    accountData.withdrawBankCode = code.val1 || '';
                    accountData.withdrawAcctNum = code.val2 || '';
                    accountData.withdrawAcctName = code.val3 || code.comCdName || '';
                } else {
                    if (!accountData.depositBankCode && !accountData.withdrawBankCode) {
                        accountData.depositBankCode = code.val1 || '';
                        accountData.depositAcctNum = code.val2 || '';
                        accountData.depositAcctName = code.val3 || code.comCdName || '';
                    }
                }
            });

            setAccountInfo(accountData);
            hasFetchedAccountInfo.current = true;
        } catch {
            setAccountInfo(null);
        }
    }, []);

    return {
        wdTypes,
        withdrawFee,
        depositFee,
        accountInfo,
        fetchAccountInfo,
    };
};
